import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Wallet, ChartBar, ArrowsClockwise, Buildings } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";

const COP = (n) => new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(n || 0);
const COPCompact = (n) => {
    const v = n || 0;
    if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    return `$${Math.round(v / 1000)}K`;
};
const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

function Stat({ label, value, highlight, mono }) {
    return (
        <div className={`p-5 border-r border-zinc-200 last:border-r-0 ${highlight ? "bg-zinc-50" : ""}`}>
            <div className="overline mb-2">{label}</div>
            <div className={`font-display text-2xl font-black tracking-tighter ${mono ? "font-mono" : ""}`}>{value}</div>
        </div>
    );
}

export default function FinancieroGeneral() {
    const [tab, setTab] = useState("activos");
    const [data, setData] = useState(null);
    const [roi, setRoi] = useState(null);
    const [anio, setAnio] = useState(new Date().getFullYear());
    const [corte, setCorte] = useState(null);
    const [fechaCorte, setFechaCorte] = useState(`${new Date().getFullYear()}-12-20`);
    const [error, setError] = useState("");
    const [errorRoi, setErrorRoi] = useState("");
    const [errorCorte, setErrorCorte] = useState("");
    const [tasaImpuesto, setTasaImpuesto] = useState(35);
    const [gastosAdmin, setGastosAdmin] = useState(null);
    const [errorGastosAdmin, setErrorGastosAdmin] = useState("");
    const [gaConcepto, setGaConcepto] = useState("");
    const [gaCategoria, setGaCategoria] = useState("salario_fijo");
    const [gaMonto, setGaMonto] = useState("");
    const [gaFecha, setGaFecha] = useState(new Date().toISOString().slice(0, 10));

    const loadGastosAdmin = () => {
        setErrorGastosAdmin("");
        api.get("/gastos-administrativos")
            .then(r => setGastosAdmin(r.data))
            .catch(e => setErrorGastosAdmin(e.response?.data?.detail || "No se pudo cargar gastos administrativos"));
    };

    const addGastoAdmin = async () => {
        if (!gaConcepto || !gaMonto) return;
        await api.post("/gastos-administrativos", { concepto: gaConcepto, categoria: gaCategoria, monto_cop: parseInt(gaMonto), fecha: gaFecha });
        setGaConcepto(""); setGaMonto("");
        loadGastosAdmin();
    };
    const delGastoAdmin = async (id) => { await api.delete(`/gastos-administrativos/${id}`); loadGastosAdmin(); };

    useEffect(() => {
        if (tab === "gastos-admin") loadGastosAdmin();
    }, [tab]);


    useEffect(() => {
        api.get("/financiero/resumen-general")
            .then(r => setData(r.data))
            .catch(e => setError(e.response?.data?.detail || "No se pudo cargar el resumen financiero"));
    }, []);

    useEffect(() => {
        if (tab !== "roi") return;
        setRoi(null); setErrorRoi("");
        api.get("/financiero/roi-anual", { params: { anio, tasa_impuesto_pct: tasaImpuesto } })
            .then(r => setRoi(r.data))
            .catch(e => setErrorRoi(e.response?.data?.detail || "No se pudo cargar el ROI anual"));
    }, [tab, anio, tasaImpuesto]);

    useEffect(() => {
        if (tab !== "corte") return;
        setCorte(null); setErrorCorte("");
        api.get("/financiero/corte-anual", { params: { fecha_corte: fechaCorte, tasa_impuesto_pct: tasaImpuesto } })
            .then(r => setCorte(r.data))
            .catch(e => setErrorCorte(e.response?.data?.detail || "No se pudo cargar el corte de año"));
    }, [tab, fechaCorte, tasaImpuesto]);

    const chartData = roi ? roi.por_mes.map(m => ({
        mes: MESES[m.mes - 1],
        Ingresos: m.ingresos_cop,
        Costos: m.costos_cop,
        Utilidad: m.utilidad_cop,
    })) : [];

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-8" data-testid="financiero-general-page">
            <div className="overline mb-1" style={{ color: "#0F766E" }}>🔒 Solo administradores</div>
            <h1 className="font-display text-4xl font-black tracking-tighter mb-6">Financiero</h1>

            <div className="flex gap-2 mb-8 border-b border-zinc-200">
                <button onClick={() => setTab("activos")} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "activos" ? "border-teal-500 text-zinc-900" : "border-transparent text-zinc-500"}`} data-testid="tab-activos">
                    Proyectos activos
                </button>
                <button onClick={() => setTab("roi")} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1.5 ${tab === "roi" ? "border-teal-500 text-zinc-900" : "border-transparent text-zinc-500"}`} data-testid="tab-roi">
                    <ChartBar size={16} /> ROI anual de ATIA
                </button>
                <button onClick={() => setTab("corte")} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1.5 ${tab === "corte" ? "border-teal-500 text-zinc-900" : "border-transparent text-zinc-500"}`} data-testid="tab-corte">
                    <ArrowsClockwise size={16} /> Corte de año
                </button>
                <button onClick={() => setTab("gastos-admin")} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1.5 ${tab === "gastos-admin" ? "border-teal-500 text-zinc-900" : "border-transparent text-zinc-500"}`} data-testid="tab-gastos-admin">
                    <Buildings size={16} /> Gastos administrativos
                </button>
            </div>

            {tab === "activos" && (
                <>
                    {error && <div className="border border-red-300 bg-red-50 p-4 text-sm text-red-700 font-mono mb-6">{error}</div>}
                    {!data && !error && <div className="text-sm text-zinc-400">Cargando…</div>}
                    {data && (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-zinc-200 mb-8">
                                <Stat label="Presupuesto total" value={COP(data.totales.presupuesto_total_cop)} mono />
                                <Stat label="Gastos totales" value={COP(data.totales.total_gastos_cop)} mono />
                                <Stat label="Pagado a técnicos" value={COP(data.totales.total_pagado_tecnicos_cop)} mono />
                                <Stat label="Comisiones totales" value={COP(data.totales.total_comision_cop)} mono />
                                <Stat label="Saldo disponible total" value={COP(data.totales.saldo_disponible_cop)} mono highlight />
                            </div>

                            {data.proyectos.length === 0 ? (
                                <div className="border border-zinc-200 p-8 text-center text-zinc-400">
                                    <Wallet size={32} className="mx-auto mb-2" />
                                    No hay proyectos activos con presupuesto asignado.
                                </div>
                            ) : (
                                <div className="border border-zinc-200 overflow-x-auto">
                                    <table className="atia-table">
                                        <thead>
                                            <tr>
                                                <th>Cliente</th>
                                                <th>Proyecto</th>
                                                <th>Presupuesto</th>
                                                <th>Gastos</th>
                                                <th>Pagado técnicos</th>
                                                <th>Comisión</th>
                                                <th>Saldo disponible</th>
                                                <th>% Ejecutado</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.proyectos.map(p => (
                                                <tr key={p.subscription_id} className="row-link" onClick={() => (window.location.href = `/clients/${p.client_id}`)}>
                                                    <td className="font-semibold">{p.razon_social}</td>
                                                    <td className="text-zinc-600 text-sm">{p.nombre_proyecto || "-"}</td>
                                                    <td className="font-mono">{COP(p.presupuesto_total_cop)}</td>
                                                    <td className="font-mono">{COP(p.total_gastos_cop)}</td>
                                                    <td className="font-mono">{COP(p.total_pagado_tecnicos_cop)}</td>
                                                    <td className="font-mono">{COP(p.comision_cop)}</td>
                                                    <td className="font-mono font-bold" style={{ color: p.saldo_disponible_cop < 0 ? "#DC2626" : "#16A34A" }}>
                                                        {COP(p.saldo_disponible_cop)}
                                                    </td>
                                                    <td className="font-mono text-xs">{p.pct_ejecutado}%</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            {tab === "roi" && (
                <>
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                        <label className="overline">Año</label>
                        <select value={anio} onChange={e => setAnio(parseInt(e.target.value))} className="input-atia w-28" data-testid="select-anio">
                            {[anio + 1, anio, anio - 1, anio - 2].sort((a, b) => b - a).map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                        <label className="overline">Tasa de impuesto</label>
                        <input type="number" value={tasaImpuesto} onChange={e => setTasaImpuesto(parseFloat(e.target.value) || 0)} className="input-atia w-20" step="0.5" min="0" max="100" data-testid="input-tasa-impuesto" />
                        <span className="text-xs text-zinc-500">%  · Editable — estimación, no es asesoría tributaria.</span>
                    </div>
                    <div className="text-xs text-zinc-500 mb-4">Incluye proyectos activos y cerrados que iniciaron en este año — vista en tiempo real.</div>

                    {errorRoi && <div className="border border-red-300 bg-red-50 p-4 text-sm text-red-700 font-mono mb-6">{errorRoi}</div>}
                    {!roi && !errorRoi && <div className="text-sm text-zinc-400">Cargando…</div>}

                    {roi && (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-zinc-200 mb-2">
                                <Stat label="Ingresos totales" value={COP(roi.ingresos_totales_cop)} mono />
                                <Stat label="Costos de proyectos" value={COP(roi.costos_totales_cop)} mono />
                                <Stat label="Utilidad de proyectos" value={COP(roi.utilidad_total_cop)} mono />
                                <Stat label="ROI del año" value={roi.roi_total_pct !== null ? `${roi.roi_total_pct}%` : "—"} mono />
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-zinc-200 mb-6" style={{ background: "#F0FDFA" }}>
                                <Stat label="Gastos administrativos" value={COP(roi.gastos_administrativos_cop)} mono />
                                <Stat label="EBITDA" value={COP(roi.ebitda_cop)} mono highlight />
                                <Stat label={`Impuesto estimado (${roi.tasa_impuesto_pct}%)`} value={COP(roi.impuesto_estimado_cop)} mono />
                                <Stat label="Utilidad neta estimada" value={COP(roi.utilidad_neta_estimada_cop)} mono highlight />
                            </div>

                            <div className="border border-zinc-200 p-4 mb-8">
                                <div className="overline mb-3">Ingresos, costos y utilidad por mes ({roi.anio})</div>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                                        <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                                        <YAxis tickFormatter={COPCompact} tick={{ fontSize: 11 }} />
                                        <Tooltip formatter={(v) => COP(v)} />
                                        <Legend />
                                        <Bar dataKey="Ingresos" fill="#0A191E" />
                                        <Bar dataKey="Costos" fill="#EF4444" />
                                        <Bar dataKey="Utilidad" fill="#14B8A6" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {roi.proyectos.length === 0 ? (
                                <div className="border border-zinc-200 p-8 text-center text-zinc-400">
                                    <ChartBar size={32} className="mx-auto mb-2" />
                                    No hay proyectos que hayan iniciado en {roi.anio}.
                                </div>
                            ) : (
                                <>
                                    <div className="overline mb-2">Proyectos ordenados por rentabilidad (los más críticos primero)</div>
                                    <div className="border border-zinc-200 overflow-x-auto">
                                        <table className="atia-table">
                                            <thead>
                                                <tr>
                                                    <th>Cliente</th>
                                                    <th>Proyecto</th>
                                                    <th>Estado</th>
                                                    <th>Ingresos</th>
                                                    <th>Costos</th>
                                                    <th>Utilidad</th>
                                                    <th>ROI %</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {roi.proyectos.map(p => (
                                                    <tr key={p.subscription_id} className="row-link" onClick={() => (window.location.href = `/clients/${p.client_id}`)}>
                                                        <td className="font-semibold">{p.razon_social}</td>
                                                        <td className="text-zinc-600 text-sm">{p.nombre_proyecto || "-"}</td>
                                                        <td className="text-xs font-mono uppercase">{p.estado_proyecto}</td>
                                                        <td className="font-mono">{COP(p.presupuesto_cop)}</td>
                                                        <td className="font-mono">{COP(p.costos_cop)}</td>
                                                        <td className="font-mono font-bold" style={{ color: p.utilidad_cop < 0 ? "#DC2626" : "#16A34A" }}>
                                                            {COP(p.utilidad_cop)}
                                                        </td>
                                                        <td className="font-mono text-xs font-bold" style={{ color: p.roi_pct === null ? "#A1A1AA" : p.roi_pct < 0 ? "#DC2626" : "#16A34A" }}>
                                                            {p.roi_pct !== null ? `${p.roi_pct}%` : "—"}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </>
            )}

            {tab === "corte" && (
                <>
                    <div className="flex items-center gap-3 mb-6 flex-wrap">
                        <label className="overline">Fecha de corte</label>
                        <input type="date" value={fechaCorte} onChange={e => setFechaCorte(e.target.value)} className="input-atia w-44" data-testid="input-fecha-corte" />
                        <label className="overline">Tasa de impuesto</label>
                        <input type="number" value={tasaImpuesto} onChange={e => setTasaImpuesto(parseFloat(e.target.value) || 0)} className="input-atia w-20" step="0.5" min="0" max="100" data-testid="input-tasa-impuesto-corte" />
                        <span className="text-xs text-zinc-500">%  · Por defecto 20 de diciembre. Tasa editable — estimación, no es asesoría tributaria.</span>
                    </div>

                    {errorCorte && <div className="border border-red-300 bg-red-50 p-4 text-sm text-red-700 font-mono mb-6">{errorCorte}</div>}
                    {!corte && !errorCorte && <div className="text-sm text-zinc-400">Cargando…</div>}

                    {corte && (
                        <>
                            {/* 1) Cómo nos fue */}
                            <div className="mb-8">
                                <div className="overline mb-1" style={{ color: "#0F766E" }}>1. Cómo nos fue</div>
                                <h2 className="font-display text-xl font-bold tracking-tight mb-3">
                                    Período {corte.corte_anterior} → {corte.fecha_corte}
                                </h2>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-zinc-200 mb-2">
                                    <Stat label="Proyectos del período" value={corte.periodo_cerrado.cantidad_proyectos} />
                                    <Stat label="Ingresos" value={COP(corte.periodo_cerrado.ingresos_cop)} mono />
                                    <Stat label="Costos proyectos" value={COP(corte.periodo_cerrado.costos_cop)} mono />
                                    <Stat label="Utilidad proyectos" value={COP(corte.periodo_cerrado.utilidad_cop)} mono />
                                    <Stat label="ROI del período" value={corte.periodo_cerrado.roi_pct !== null ? `${corte.periodo_cerrado.roi_pct}%` : "—"} mono />
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-zinc-200" style={{ background: "#F0FDFA" }}>
                                    <Stat label="Gastos administrativos" value={COP(corte.periodo_cerrado.gastos_administrativos_cop)} mono />
                                    <Stat label="EBITDA" value={COP(corte.periodo_cerrado.ebitda_cop)} mono highlight />
                                    <Stat label={`Impuesto estimado (${corte.periodo_cerrado.tasa_impuesto_pct}%)`} value={COP(corte.periodo_cerrado.impuesto_estimado_cop)} mono />
                                    <Stat label="Utilidad neta estimada" value={COP(corte.periodo_cerrado.utilidad_neta_estimada_cop)} mono highlight />
                                </div>
                            </div>

                            {/* 2) Cómo pasamos al siguiente período */}
                            <div className="mb-8">
                                <div className="overline mb-1" style={{ color: "#0F766E" }}>2. Cómo pasamos al siguiente período</div>
                                <h2 className="font-display text-xl font-bold tracking-tight mb-1">
                                    Proyectos que siguen abiertos ({corte.proyectos_continuan.length})
                                </h2>
                                <div className="text-sm text-zinc-600 mb-3">
                                    Saldo pendiente total que "viaja" al siguiente período: <span className="font-mono font-bold">{COP(corte.saldo_pendiente_total_cop)}</span>
                                </div>
                                {corte.proyectos_continuan.length === 0 ? (
                                    <div className="border border-zinc-200 p-6 text-center text-zinc-400 text-sm">No hay proyectos abiertos en este momento.</div>
                                ) : (
                                    <div className="border border-zinc-200 overflow-x-auto">
                                        <table className="atia-table">
                                            <thead>
                                                <tr>
                                                    <th>Cliente</th>
                                                    <th>Proyecto</th>
                                                    <th>Estado</th>
                                                    <th>Presupuesto</th>
                                                    <th>Gastado a la fecha</th>
                                                    <th>Saldo pendiente</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {corte.proyectos_continuan.map(p => (
                                                    <tr key={p.subscription_id} className="row-link" onClick={() => (window.location.href = `/clients/${p.client_id}`)}>
                                                        <td className="font-semibold">{p.razon_social}</td>
                                                        <td className="text-zinc-600 text-sm">{p.nombre_proyecto || "-"}</td>
                                                        <td className="text-xs font-mono uppercase">{p.estado_proyecto}</td>
                                                        <td className="font-mono">{COP(p.presupuesto_cop)}</td>
                                                        <td className="font-mono">{COP(p.gastado_a_la_fecha_cop)}</td>
                                                        <td className="font-mono font-bold" style={{ color: p.saldo_pendiente_cop < 0 ? "#DC2626" : "#16A34A" }}>{COP(p.saldo_pendiente_cop)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            {/* 3) Cómo arrancamos */}
                            <div>
                                <div className="overline mb-1" style={{ color: "#0F766E" }}>3. Cómo arrancamos el nuevo período</div>
                                <h2 className="font-display text-xl font-bold tracking-tight mb-1">
                                    Proyectos nuevos ({corte.proyectos_nuevos.length})
                                </h2>
                                <div className="text-sm text-zinc-600 mb-3">
                                    Ingresos ya comprometidos en el nuevo período: <span className="font-mono font-bold">{COP(corte.ingresos_nuevos_total_cop)}</span>
                                </div>
                                {corte.proyectos_nuevos.length === 0 ? (
                                    <div className="border border-zinc-200 p-6 text-center text-zinc-400 text-sm">Todavía no hay proyectos nuevos registrados después de esta fecha.</div>
                                ) : (
                                    <div className="border border-zinc-200 overflow-x-auto">
                                        <table className="atia-table">
                                            <thead>
                                                <tr>
                                                    <th>Cliente</th>
                                                    <th>Proyecto</th>
                                                    <th>Fecha inicio</th>
                                                    <th>Presupuesto</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {corte.proyectos_nuevos.map(p => (
                                                    <tr key={p.subscription_id} className="row-link" onClick={() => (window.location.href = `/clients/${p.client_id}`)}>
                                                        <td className="font-semibold">{p.razon_social}</td>
                                                        <td className="text-zinc-600 text-sm">{p.nombre_proyecto || "-"}</td>
                                                        <td className="font-mono text-xs">{p.fecha_inicio?.slice(0, 10)}</td>
                                                        <td className="font-mono">{COP(p.presupuesto_cop)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </>
            )}

            {tab === "gastos-admin" && (
                <>
                    <p className="text-sm text-zinc-600 mb-6">
                        Costos fijos que impactan a TODA la operación de ATIA, no a un proyecto específico:
                        salarios fijos (Juan Carlos, Ronal, Andrés), arriendo de bodega/oficina, servicios públicos, seguros, software.
                        Estos valores se descuentan de la utilidad de proyectos para calcular el EBITDA en las pestañas de ROI anual y Corte de año.
                    </p>

                    {errorGastosAdmin && <div className="border border-red-300 bg-red-50 p-4 text-sm text-red-700 font-mono mb-6">{errorGastosAdmin}</div>}
                    {!gastosAdmin && !errorGastosAdmin && <div className="text-sm text-zinc-400">Cargando…</div>}

                    {gastosAdmin && (
                        <>
                            <div className="flex flex-wrap gap-2 mb-6 border border-zinc-200 p-4">
                                <input value={gaConcepto} onChange={e => setGaConcepto(e.target.value)} placeholder="Concepto (ej. Salario Juan Carlos - Agosto)" className="input-atia flex-1 min-w-[220px]" data-testid="ga-concepto" />
                                <select value={gaCategoria} onChange={e => setGaCategoria(e.target.value)} className="input-atia" data-testid="ga-categoria">
                                    <option value="salario_fijo">Salario fijo</option>
                                    <option value="arriendo">Arriendo</option>
                                    <option value="servicios_publicos">Servicios públicos</option>
                                    <option value="seguros">Seguros</option>
                                    <option value="software">Software</option>
                                    <option value="garantia">Garantía</option>
                                    <option value="multa_sancion">Multa / Sanción</option>
                                    <option value="imprevisto">Imprevisto</option>
                                    <option value="otro">Otro</option>
                                </select>
                                <input type="date" value={gaFecha} onChange={e => setGaFecha(e.target.value)} className="input-atia w-40" data-testid="ga-fecha" />
                                <input value={gaMonto} onChange={e => setGaMonto(e.target.value)} placeholder="Monto COP" type="number" className="input-atia w-36" data-testid="ga-monto" />
                                <button onClick={addGastoAdmin} className="btn-accent text-sm" data-testid="add-gasto-admin">+ Agregar</button>
                            </div>

                            {gastosAdmin.length === 0 ? (
                                <div className="border border-zinc-200 p-8 text-center text-zinc-400">
                                    <Buildings size={32} className="mx-auto mb-2" />
                                    Sin gastos administrativos registrados todavía.
                                </div>
                            ) : (
                                <div className="border border-zinc-200 overflow-x-auto">
                                    <table className="atia-table">
                                        <thead>
                                            <tr>
                                                <th>Fecha</th>
                                                <th>Concepto</th>
                                                <th>Categoría</th>
                                                <th>Monto</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {gastosAdmin.map(g => (
                                                <tr key={g.gasto_admin_id}>
                                                    <td className="font-mono text-xs">{g.fecha?.slice(0, 10)}</td>
                                                    <td className="font-semibold">{g.concepto}</td>
                                                    <td className="text-xs font-mono uppercase text-zinc-500">{g.categoria.replace("_", " ")}</td>
                                                    <td className="font-mono">{COP(g.monto_cop)}</td>
                                                    <td>
                                                        <button onClick={() => delGastoAdmin(g.gasto_admin_id)} className="text-red-500 hover:text-red-700 text-xs" data-testid={`del-ga-${g.gasto_admin_id}`}>✕</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
}
